import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center rounded-lg text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        // Default primary action button - filled background with primary color
        default:
          'bg-primary text-white hover:bg-primary/90 border-transparent shadow-sm focus:ring-primary/70',
        // Explicitly primary variant (same as default for consistency)
        primary:
          'bg-primary text-white hover:bg-primary/90 border-transparent shadow-sm focus:ring-primary/70',
        // Destructive actions (delete, remove, etc.)
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-sm',
        // Secondary actions - white background with border (like non-primary Quick Actions)
        outline:
          'border bg-white hover:bg-muted text-muted-foreground hover:text-foreground border-border focus:ring-primary',
        // Alternative secondary style with subtle background
        secondary:
          'border bg-secondary hover:bg-secondary/80 text-secondary-foreground border-border focus:ring-primary',
        // Ghost buttons for minimal emphasis
        ghost:
          'hover:bg-accent hover:text-accent-foreground',
        // Link style buttons
        link: 'text-primary underline-offset-4 hover:underline',
        // For special brand color buttons
        brand:
          'bg-terrarium-orange text-white hover:bg-terrarium-orange/90 shadow-md hover:shadow-lg',
      },
      size: {
        default: 'h-10 px-4 py-2.5',
        sm: 'h-9 rounded-md px-3 text-xs',
        lg: 'h-11 rounded-lg px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
